import { Component, HostBinding, Input, OnChanges } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, ReportBlock, chartColors, paletteColors } from '../report-aggregation';
import { Band, bandScale, bandsBestFirst, bandsOf, netOfBands } from './scale-bands';
import type { BlockPatch } from '../report-design-edit';
import { NO_VALUE } from './summary-boxes.component';

const DEFAULT_LABEL = 'Brand Advocacy Score';
const DEFAULT_SCALE_MAX = 100;

// A true semicircle, thick enough to carry a band of colour rather than to read as a hairline
// rule. It was a wide, shallow ellipse while the arc was a single fill and the figure sat inside it;
// now the arc is divided between the bands and the shares stand beside it, so the ring is the
// subject and has the width to say so.
//
// Drawn by hand rather than with a Highcharts gauge, which needs the highcharts-more and solid-gauge
// modules this app does not load; an arc is a single path.
const CX = 100;
const CY = 100;
const R = 82;
const STROKE = 12;

/** The svg's viewBox, which a tooltip over the ring is placed against. */
const VIEW_WIDTH = 200;
const VIEW_HEIGHT = 104;

/** Half a circle's circumference, which -- unlike an ellipse -- is exact. */
const ARC_LENGTH = Math.PI * R;

/** Left end to right end, over the top. */
const ARC = `M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`;

/**
 * The trend mark, as two paths in an 18-square box: the line the score traced, and the arrowhead at
 * its end. Down is up mirrored about the middle, so the pair read as one shape turned over.
 */
const TREND = {
  up: { line: 'M2 13.5 L6.5 9 L9.5 12 L16 4.5', head: 'M11.5 4.5 H16 V9' },
  down: { line: 'M2 4.5 L6.5 9 L9.5 6 L16 13.5', head: 'M11.5 13.5 H16 V9' },
  flat: { line: 'M2.5 9 H15.5', head: '' },
};

/** A band as the card reads it: rounded, and coloured as its segment of the ring. */
interface BandReading {
  label: string;
  share: number;
  range: string;
  colour: string;
}

/** A band that owns none of the scale, and the whole ring before anything is computed. */
const INERT = 'var(--ecr-grey, #c3ced3)';

@Component({
  selector: 'app-single-kpi-dial-block',
  standalone: false,
  template: `
    <div class="kpi">
    @if (figure) {
      <!-- The score alone. For a net drawn under a chart that has already shown the same answers --
           a stacked distribution above it -- where a ring dividing them again would say the same
           thing twice. The label, the two written lines and the score are all edited in place, like
           the dial's: they are the content of the card rather than settings about how it is drawn. -->
      <div class="figure-block">
        <div class="figure-panel" [style.border-color]="frameColour">
          <!-- Derived, not chosen: the mark follows the sign of the score, so it cannot end up
               pointing up under a figure that fell. An icon field would be a setting whose right
               answer the number already knows.

               Drawn here rather than taken from Font Awesome. The kit script swaps every <i> it
               finds for an inline <svg>, so a bound class updates a node that has been replaced and
               the icon never changes -- and rendering a fresh <i> per direction did not help
               either, since the swap has already happened by the time the class would matter. Two
               paths of our own answer to the binding directly, and stop the card depending on a
               CDN script's version for a shape this simple. -->
          <span class="figure-icon" aria-hidden="true"
                [style.border-color]="frameColour" [style.color]="frameColour">
            <svg class="trend" viewBox="0 0 18 18">
              <path [attr.d]="trendLine"></path>
              <path [attr.d]="trendHead"></path>
            </svg>
          </span>

          <div class="figure-text">
            <!-- The heading and the verdict are both built from the one word the score is a net
                 of, and the verdict from the sign of the score, so a figure that turns negative
                 cannot be left with "Positive" written under it. Only the caption is typed. -->
            <p class="figure-label mb-0">{{ heading }}</p>

            @if (editing) {
              <input type="text"
                     class="dial-input figure-caption w-100 mt-1"
                     maxlength="80"
                     aria-label="Caption above the score"
                     placeholder="A line about what this means, if you want one"
                     [value]="caption"
                     (input)="set('caption', $any($event.target).value)">
              <input type="text"
                     class="dial-input figure-value font-weight-bold w-100 mt-2"
                     [style.color]="valueColour"
                     maxlength="12"
                     aria-label="Score"
                     [attr.placeholder]="shownValue() || noValue"
                     [value]="value"
                     (input)="set('value', $any($event.target).value)">
            } @else {
              @if (caption) {
                <p class="figure-caption mb-0">{{ caption }}</p>
              }
              <p class="figure-value font-weight-bold mb-0" [style.color]="valueColour">
                {{ shownValue() || noValue }}
              </p>
            }

            @if (verdict) {
              <p class="figure-verdict mb-0" [style.color]="valueColour">{{ verdict }}</p>
            }
          </div>
        </div>

        <!-- Under the panel, not inside it: the panel is the reading and this is its working.
             Switched off where the page has already shown the sum -- a net sitting under the very
             distribution it was computed from does not need to restate it. -->
        @if (footnote) {
          <p class="dial-note text-center mb-0">{{ footnote }}</p>
        }
      </div>
    } @else {
    <div class="dial">
      <div class="dial-ring">
      <svg class="dial-svg" [attr.viewBox]="'0 0 ' + viewWidth + ' ' + viewHeight" role="img" [attr.aria-label]="ariaLabel">
        <path class="dial-track" [attr.d]="arc" [attr.stroke-width]="stroke"></path>

        <!-- Banded, the ring *is* the composition: each band takes the share of the half-circle it
             holds, reading best-first from the left, so the arc and the figures beside it are two
             views of one thing. Unbanded -- a dial somebody typed a number into -- it falls back to
             the single fill it has always drawn, from the floor of the scale to the score. -->
        @if (segments.length) {
          @for (segment of segments; track $index) {
            <path class="dial-fill is-hoverable"
                  [attr.d]="arc"
                  [attr.stroke]="segment.fill"
                  [attr.stroke-width]="stroke"
                  [attr.stroke-dasharray]="segment.length + ' ' + arcLength"
                  [attr.stroke-dashoffset]="-segment.start"
                  (mouseenter)="hovered = $index"
                  (mouseleave)="hovered = null"></path>
          }
        } @else {
          <path class="dial-fill is-capped"
                [attr.d]="arc"
                [attr.stroke]="colour"
                [attr.stroke-width]="stroke"
                [attr.stroke-dasharray]="arcLength"
                [attr.stroke-dashoffset]="dashOffset"></path>
        }
      </svg>

      <!-- The hovered band, as the deck's chart tooltips are drawn: over the middle of its own
           segment, edged in its colour. Its name and share only -- the answers it covers are in the
           list beside the ring, and a tooltip carrying them grew large enough to distract. Drawn
           rather than announced: the band list says the same thing to a screen reader. -->
      @if (hoveredTip; as tip) {
        <span class="tip" aria-hidden="true"
              [style.left.%]="tip.x" [style.top.%]="tip.y" [style.border-color]="tip.colour">
          <span class="tip-head">{{ tip.label || 'Band' }}</span>
          <span class="tip-body">
            <span class="tip-dot" [style.color]="tip.colour">●</span><b>{{ tip.share }}%</b>
          </span>
        </span>
      }

      <!-- Overlaid rather than SVG <text>: these are editable in place, and an HTML input inside an
           SVG would need a foreignObject that browsers size inconsistently. -->
      <div class="dial-content text-center">
        @if (editing) {
          <input type="text"
                 class="dial-input dial-value font-weight-bold w-100 text-center"
                 [style.color]="valueColour"
                 maxlength="12"
                 aria-label="Score"
                 [attr.placeholder]="computed || noValue"
                 [value]="value"
                 (input)="set('value', $any($event.target).value)">
          <input type="text"
                 class="dial-input dial-label w-100 text-center"
                 maxlength="60"
                 aria-label="Score label"
                 [attr.placeholder]="defaultLabel"
                 [value]="rawLabel"
                 (input)="set('label', $any($event.target).value)">
        } @else {
          <p class="dial-value font-weight-bold mb-0" [style.color]="valueColour">{{ shownValue() || noValue }}</p>
          <p class="dial-label mb-0">{{ label }}</p>
        }
      </div>
      </div>

      <!-- In the flow under the ring rather than overlaid in it: the opening only has room for the
           figure and its name, and a definition three lines long pushed both off centre. Still
           inside the dial column, so it stays under the thing it defines rather than running the
           width of the card. -->
      @if (footnote) {
        <p class="dial-note text-center mb-0">{{ footnote }}</p>
      }
    </div>
    }

    <!-- The bands as figures, beside the ring that draws them. One row each: the swatch ties the
         row to its arc segment, the share is the reading, and the answers it covers are set back in
         grey -- the check on whether the band was drawn where its author meant it. -->
    @if (shares.length && !figure) {
      <ul class="band-list list-unstyled mb-0">
        @for (band of shares; track band.label) {
          <li class="band-row">
            <p class="band-name mb-0">
              <span class="band-dot" [style.background-color]="band.colour"></span>{{ band.label }}
            </p>
            <p class="band-figure mb-0 mx-3">
              <span class="band-share font-weight-bold" [style.color]="band.colour">{{ band.share }}%</span>
              <!-- One line, cut short: a scale worded as sentences ran to three lines a band and
                   pushed the figures apart. The full wording is still in the text, for a screen
                   reader; the cut is only drawn. -->
              @if (band.range) {
                <span class="band-range ml-auto">({{ band.range }})</span>
              }
            </p>
          </li>
        }
      </ul>
    }
    </div>

    @if (editing) {
      @for (problem of problems; track problem) {
        <p class="block-note text-center mb-0 mt-2">{{ problem }}</p>
      }
    }
  `,
  styles: [`
    :host { display: block; }
    /* Ring and figures side by side, wrapping to stacked where a half-width block cannot hold both.
       The gap is what separates them; neither carries a margin of its own. */
    .kpi {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: 1.5rem;
    }
    /* The content sits over the arc, in a box sized by the svg. A container as well, so the figure
       can be sized from the dial's own width: the SVG is a fixed viewBox stretched to fit, so
       anything measured in px drifts out of proportion the moment the window resizes. */
    .dial {
      container-type: inline-size;
      flex: 1 1 220px;
      max-width: 320px;
    }
    .dial-ring {
      position: relative;
    }
    .dial-svg {
      display: block;
      width: 100%;
      overflow: visible;
    }
    /* The card's rule colour, a step lighter than the inert grey the last band takes. They have to
       differ: bands that leave answers unclaimed -- a trailing "Not sure" -- stop short of closing
       the ring, and in one grey that gap would read as part of the bottom band. */
    .dial-track {
      fill: none;
      stroke: var(--ecr-line, #dbe4e8);
    }
    /* Butt ends, not round: the segments meet, and a rounded cap on each would overlap its
       neighbour and read as a bulge at every boundary. The single unbanded fill keeps its round cap,
       where there is no neighbour to run into. */
    .dial-fill {
      fill: none;
      transition: stroke-dashoffset 200ms ease;
    }
    .dial-fill.is-capped {
      stroke-linecap: round;
    }
    .dial-fill.is-hoverable {
      cursor: default;
    }
    /* Inside the arc rather than under it: the gauge is meant to surround the figure. */
    .dial-content {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 4%;
      padding: 0 14%;
      /* It spans the ring's two ends, so it would take their hover; its inputs still take clicks. */
      pointer-events: none;
    }
    .dial-content input {
      pointer-events: auto;
    }
    /* cqw is 1% of the dial's width, so the figure keeps its proportion to the arc at every size;
       clamp stops it going unreadable in a half-width slot or running away on a wide monitor. The
       tuning point is the top: 8.5cqw of a full-width block lands on the 72px that looked right.
       Content is anchored to the bottom of the arc, so the margin lifts the figure clear of its
       label rather than pushing the label down. Both !important -- the element carries mb-0. */
    .dial-value {
      line-height: 1.05;
      font-size: clamp(28px, 17cqw, 60px) !important;
      margin-bottom: clamp(0.5rem, 5cqw, 1.4rem) !important;
    }
    /* Set as a heading for the figure above it rather than as a caption: small, bold and spaced, the
       same treatment the deck's own subheadings take. */
    .dial-label {
      color: var(--ecr-ink, #26414c);
      font-size: clamp(9px, 4.2cqw, 13px) !important;
      font-weight: 700;
      letter-spacing: 0.4px;
      line-height: 1.25;
      text-transform: uppercase;
    }
    /* pre-line so the break the note carries is honoured, while the rest still wraps to the
       column. Flat 12px rather than sized off the dial: this is read, not scaled against the ring. */
    .dial-note {
      color: var(--ecr-mut, #5a6b73);
      font-size: 12px !important;
      line-height: 1.35;
      margin-top: 0.35rem;
      white-space: pre-line;
    }
    .dial-input {
      background: transparent;
      border: 1px solid var(--nav-grey1);
      border-radius: 3px;
      color: inherit;
      padding: 0 2px;
    }
    .dial-input:hover {
      border-color: var(--nav-grey2);
    }
    .dial-input:focus {
      background: var(--nav-true-white);
      border-color: var(--nav-grey3);
      outline: none;
    }
    /* The score without its ring. Sized in rem rather than in container units: there is no arc
       here for the figure to keep its proportion to, and a card holding only this should not print
       it at ninety pixels because the card happens to be wide. */
    /* Set down from the card's heading rather than tucked straight under it. The panel is one
       small object in a card that gives it no other content to sit against, so without this it
       reads as having been pushed to the top rather than placed. More above than below, since the
       explanation line hangs under it. */
    .figure-block {
      flex: 1 1 100%;
      margin: 1.75rem 0 0.75rem;
      max-width: 24rem;
    }
    /* A panel inside the card rather than the card itself. It reads as a conclusion drawn from what
       is above it -- on a trajectory page the stacked bar is the evidence and this is the finding --
       so it is set apart rather than sharing the card's white. */
    .figure-panel {
      align-items: center;
      background: var(--ecr-bg, #eef3f5);
      border: 1px solid var(--ecr-line, #dbe4e8);
      border-radius: 12px;
      display: flex;
      gap: 1.6rem;
      padding: 1.4rem 1.75rem;
    }
    /* currentColor, so the mark takes the panel's ink without a second colour to keep in step. */
    .trend {
      fill: none;
      height: 28px;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.6;
      width: 28px;
    }
    .figure-icon {
      align-items: center;
      border: 1px solid var(--ecr-grey, #c3ced3);
      border-radius: 50%;
      color: var(--ecr-ink, #26414c);
      display: flex;
      flex: 0 0 auto;
      height: 58px;
      justify-content: center;
      width: 58px;
    }
    /* min-width:0 so a long label ellipsises instead of pushing the icon off the panel. */
    .figure-text {
      flex: 1 1 auto;
      min-width: 0;
    }
    .figure-label {
      color: var(--ecr-ink, #26414c);
      font-size: var(--ecr-font-card, 15px);
      font-weight: 700;
      letter-spacing: 0.3px;
      line-height: 1.25;
    }
    .figure-caption {
      color: var(--ecr-mut, #5a6b73);
      font-size: var(--ecr-font-sm, 12px);
      font-style: italic;
      line-height: 1.3;
    }
    /* Set close under the heading, then a clear step down to the score: the first two lines name
       the figure and the third is the figure, so the space belongs between them rather than evenly. */
    .figure-value {
      font-size: 2.9rem;
      line-height: 1.05;
      margin-top: 0.6rem !important;
    }
    .figure-verdict {
      font-size: var(--ecr-font-sm, 12px);
      font-weight: 700;
      margin-top: 0.35rem !important;
    }
    /* Same rule as the host's .block-note. Duplicated for the reason the host duplicates
       .inline-edit: view encapsulation stops its stylesheet reaching in here. */
    .block-note {
      color: var(--ecr-mut, #5a6b73);
      font-size: var(--ecr-font-xs, 11px);
    }

    /* The figures beside the ring. Ruled between rows rather than boxed: the rule is enough to
       separate three readings, and a border round each would compete with the card. */
    .band-list {
      flex: 1 1 180px;
      max-width: 300px;
    }
    .band-row {
      padding: 0.45rem 0;
    }
    .band-row + .band-row {
      border-top: 1px solid var(--ecr-line, #dbe4e8);
    }
    .band-name {
      color: var(--ecr-ink, #26414c);
      font-size: var(--ecr-font-sm, 12px);
      font-weight: 600;
    }
    .band-dot {
      border-radius: 50%;
      display: inline-block;
      height: 9px;
      margin-right: 7px;
      vertical-align: baseline;
      width: 9px;
    }
    /* Baseline-aligned rather than centred, so the range sits on the figure's foot the way a unit
       does, and does not drift with the figure's size. */
    .band-figure {
      align-items: baseline;
      display: flex;
      gap: 0.4rem;
    }
    .band-share {
      font-size: var(--ecr-font-kpi, 26px);
      line-height: 1.1;
    }
    /* The deck's chart tooltip, as the stage matrix draws it too: a small white card, a hairline in
       the band's colour, its name above and the figure below with a dot beside it. Pinned over the
       middle of the hovered segment and lifted clear of the ring. */
    .tip {
      position: absolute;
      z-index: 5;
      transform: translate(-50%, calc(-100% - 10px));
      width: max-content;
      max-width: 200px;
      padding: 7px 10px;
      border: 1px solid var(--nav-grey2, #ddd);
      border-radius: 3px;
      background: var(--nav-true-white, #fff);
      box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
      text-align: left;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.5;
      color: var(--ecr-ink, #1b2b33);
      white-space: normal;
      pointer-events: none;
    }
    .tip-head, .tip-body { display: block; }
    .tip-head { font-size: 11px; font-weight: 700; }
    .tip-dot { margin-right: 5px; font-size: 14px; line-height: 1; }
    .band-range {
      color: var(--ecr-mut, #5a6b73);
      font-size: var(--ecr-font-xs, 11px);
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `]
})
export class SingleKpiDialBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() alignCount?: number;
  @Input() compact?: boolean;
  @Input() editing?: boolean;
  @Input() onPatch?: (patch: BlockPatch) => void;

  @HostBinding('class.pl-5')
  @HostBinding('class.pr-5')
  @HostBinding('class.is-full')
  get fullWidth(): boolean {
    return this.block?.segmentLocation === 'full';
  }

  readonly arc = ARC;
  readonly viewWidth = VIEW_WIDTH;
  readonly viewHeight = VIEW_HEIGHT;
  readonly arcLength = ARC_LENGTH;
  readonly defaultLabel = DEFAULT_LABEL;
  readonly noValue = NO_VALUE;
  readonly stroke = STROKE;

  /** Exactly what is stored, so the edit input does not fight the placeholder. */
  value = '';
  rawLabel = '';
  caption = '';
  /** Drawn as the score alone, with no ring and no band list beside it. */
  figure = false;
  /** "Net Momentum", from the word the score is a net of. */
  heading = '';
  /** "Positive momentum", from that same word and the sign of the score. */
  verdict = '';
  /** The figure panel's border, its icon ring and the mark inside it. */
  frameColour = '';
  /** Whether the score is a net, which is the only kind that carries a sign. */
  private isNet = true;
  /** The mark beside the figure: a trend line and its arrowhead, by the sign of the score. */
  trendLine = TREND.flat.line;
  trendHead = TREND.flat.head;
  label = DEFAULT_LABEL;
  scaleMax = DEFAULT_SCALE_MAX;
  scaleMin = 0;
  colour = '';
  valueColour = '';
  dashOffset = ARC_LENGTH;
  ariaLabel = '';

  /** What the data says, blank when nothing can be derived. Shown as the input's placeholder. */
  computed = '';
  /** "% Promoters (8-10) minus % Detractors (1-5). Base n=154." */
  footnote = '';
  /** The bands best-first, rounded, for the figures beside the ring. */
  shares: BandReading[] = [];
  /** The same bands as arcs, each holding its share of the half-circle. */
  segments: { fill: string; length: number; start: number; x: number; y: number }[] = [];

  /** Which segment the pointer is over, for its tooltip. */
  hovered: number | null = null;

  /** The hovered segment's band, and where over the ring its tooltip goes. */
  get hoveredTip(): (BandReading & { x: number; y: number }) | null {
    if (this.hovered === null) return null;
    const segment = this.segments[this.hovered];
    const band = this.shares[this.hovered];
    if (!segment || !band) return null;
    return { ...band, x: segment.x, y: segment.y };
  }
  problems: string[] = [];

  ngOnChanges() {
    if (this.block.reportType !== 'kpi-score') return;
    const extra = this.block.reportExtraData;
    const { value, label, scaleMax, scaleMin, metric } = extra;
    const isNet = (metric ?? 'net') === 'net';

    this.value = value ?? '';
    this.rawLabel = label ?? '';
    this.label = label?.trim() || DEFAULT_LABEL;
    this.caption = extra.caption ?? '';
    this.figure = extra.display === 'figure';
    this.isNet = isNet;

    this.problems = [];
    this.derive(isNet);
    this.dropNoteUnderTypedFigure();

    this.scaleMax = scaleMax && scaleMax > 0 ? scaleMax : DEFAULT_SCALE_MAX;
    // A net runs from -100 to +100, so an empty gauge is not zero. Number.isFinite rather than the
    // truthiness test above, which would throw away both -100 and 0 -- the two values this field
    // exists to carry.
    this.scaleMin = Number.isFinite(scaleMin)
      ? scaleMin as number
      : (isNet && this.computed ? -DEFAULT_SCALE_MAX : 0);

    // Secondary, not primary: the arc is the scale behind the figure rather than the subject.
    const [primary, secondary] = paletteColors(this.palette);
    this.colour = secondary;
    // The palette's first role, always -- which is also what the top band takes, so the score and
    // the band it is mostly made of read as one thing. Colouring it by sign was tried and dropped:
    // a figure that changes colour when a client filters reads as a status light, and the ring
    // beside it already says which way the score fell.
    // The dial's score takes the first role, which is also the top band's, so the figure and the
    // arc it sits in read as one thing. The figure form has no ring and no bands beside it: what it
    // has is a heading in the deck's ink directly above it, and a score in that same near-black
    // would read as more of the heading. The second role is what separates them.
    this.valueColour = this.figure ? secondary : primary;
    // The panel's frame, its icon ring and the mark inside it, all the palette's first role -- the
    // trend takes it through currentColor. The neutral line and grey these used were the deck's
    // derived surfaces, which is right for a card's own border but leaves a panel *inside* a card
    // looking like an empty box rather than something the client's palette drew.
    this.frameColour = primary;
    const score = Number.parseFloat(this.shownValue());

    this.nameFigure(isNet, score);

    // Filled from what is on the card, not from whichever of the typed and the computed figure
    // happens to exist: an override of 72 over a computed 61 has to draw a 72 arc, or the gauge
    // contradicts the number inside it.
    //
    // A value that is not a number -- or is blank, which is how the block starts life -- leaves the
    // gauge empty rather than throwing a NaN dash offset at the DOM, which would render the arc
    // fully drawn.
    const span = this.scaleMax - this.scaleMin;
    const fraction = Number.isFinite(score) && span > 0
      ? Math.min(1, Math.max(0, (score - this.scaleMin) / span))
      : 0;

    this.dashOffset = ARC_LENGTH * (1 - fraction);
    const shown = this.shownValue();
    // The scale is only part of the reading where the ring is a fill against one. Banded, it draws
    // the bands' own shares and the bounds move nothing, so naming them would describe a scale the
    // figure is not on.
    this.ariaLabel = !shown ? `${this.label}: no score`
      : this.segments.length ? `${this.label}: ${shown}`
      : `${this.label}: ${shown} on a scale from ${this.scaleMin} to ${this.scaleMax}`;
  }

  /**
   * The figure form's heading and the line under it, both from the one word the score is a net of.
   *
   * A share is not a net, so it takes the word alone -- "Momentum" rather than "Net Momentum" -- and
   * carries no verdict, having no direction to report. A net of exactly zero carries none either:
   * it is neither positive nor negative, and rounding one to the other is the kind of small lie
   * that ends up on a client slide.
   */
  private nameFigure(isNet: boolean, score: number) {
    this.heading = '';
    this.verdict = '';
    // Flat covers a share, which has no direction, and a net with no score yet -- an arrow either
    // way there would assert something the card has not worked out.
    const shape = TREND[isNet && Number.isFinite(score) && score !== 0
      ? (score > 0 ? 'up' : 'down')
      : 'flat'];
    this.trendLine = shape.line;
    this.trendHead = shape.head;
    if (!this.figure) return;

    const word = (this.block.reportType === 'kpi-score'
      ? this.block.reportExtraData.netLabel ?? ''
      : '').trim();
    if (!word) {
      // Falls back to the dial's own subheading rather than heading itself "Net" with nothing after
      // it -- but says so, because the missing word is also why there is no line under the figure.
      this.heading = this.label;
      this.problems.push('Set "Net of" to one word — Momentum, Agree, Advocacy — and this heads '
        + 'itself "Net Momentum" with "Positive momentum" under the figure.');
      return;
    }

    this.heading = isNet ? `Net ${capitalise(word)}` : capitalise(word);

    // Every reason the line under the figure can be absent, said out loud while editing. It is
    // derived from three things at once -- the metric, the word and the sign -- so a blank one has
    // three possible causes and no way for whoever is looking at it to tell which.
    if (!isNet) {
      this.problems.push('A share has no direction, so there is no "Positive" or "Negative" to '
        + 'report under it. Switch Score to Net for that line.');
      return;
    }
    if (!Number.isFinite(score)) {
      this.problems.push('No score yet, so nothing to call positive or negative. Choose a question '
        + 'and band it, or type a figure on the card.');
      return;
    }
    if (score === 0) {
      this.problems.push('A net of exactly zero is neither positive nor negative, so no line is '
        + 'drawn under it.');
      return;
    }
    this.verdict = `${score > 0 ? 'Positive' : 'Negative'} ${lowerFirst(word)}`;
  }

  /**
   * What the card prints: the typed figure wins, then the computed one, then a dash.
   *
   * A positive net is printed with its sign in the figure form and without one in the dial. The ring
   * is what says which way a dialled score fell, and a + in front of a figure that large read as
   * part of the number; the bare figure has no ring, so the sign does the work instead.
   *
   * Only a net, never a share -- "+73%" of anything is not a thing -- and never over a figure that
   * already carries a sign of its own, which a typed one may.
   */
  shownValue(): string {
    const shown = this.value.trim() || this.computed;
    const signable = this.figure && this.isNet && !/^[+-]/.test(shown);
    return signable && Number.parseFloat(shown) > 0 ? `+${shown}` : shown;
  }

  /**
   * The score, the shares line and the footnote, from the question the block charts.
   *
   * Everything here is skipped where no question is bound -- `blockVariableKeys` returns nothing for
   * such a dial, so `series` is empty and the block stays the typed figure it has always been.
   */
  private derive(isNet: boolean) {
    this.computed = '';
    this.footnote = '';
    this.shares = [];
    this.segments = [];
    this.hovered = null;

    const extra = this.block.reportType === 'kpi-score' ? this.block.reportExtraData : null;
    const series = this.series?.[0];
    if (!extra?.variableKey || !series) return;

    if (!series.known) {
      this.problems.push('That question is not in this survey, so there is no score to compute.');
      return;
    }
    if (!extra.bands?.length) {
      this.problems.push('Add bands to compute a score: name the groups of answers that count as '
        + 'the top and the bottom of the scale.');
      return;
    }

    const scale = bandScale(series.points, extra.netExclude);
    const bands = bandsOf(scale, extra.bands);
    if (!bands) {
      // Blank, not truncated. The ladder shortens a stage that runs off the end of its scale and is
      // right to -- a drawing one column short still reads. A net summed over a scale the bands were
      // not written for is a wrong figure, and a wrong figure published to a client is not a
      // degraded version of a right one.
      this.problems.push(`The bands cover more answers than this question has (${scale.length}).`);
      return;
    }

    const topFirst = (extra.topBand ?? 'first') === 'first';
    const ordered = bandsBestFirst(bands, topFirst);

    // Best-first, so the ring reads left to right in the order the figures read top to bottom. The
    // last band takes the deck's inert grey rather than the next palette role: on a gauge the
    // trailing segment is the part not won, and colouring it as another category invites a reader to
    // weigh it against the first. Everything before it follows the palette, by the same call the
    // ladder and the banded histogram make, so three modules banded alike are coloured alike.
    const fills = chartColors(this.palette, Math.max(1, ordered.length));
    const colourOf = (index: number) => index === ordered.length - 1 && ordered.length > 1
      ? INERT
      : fills[index];

    this.shares = ordered.map((band, index) => ({
      label: band.label,
      share: Math.round(band.share),
      range: band.range,
      colour: colourOf(index),
    }));

    // Laid along the arc in the same order. Measured from the real shares rather than from the
    // rounded ones on show, so the segments still meet where the printed figures sum to 99 or 101.
    let start = 0;
    this.segments = ordered.map((band, index) => {
      const length = ARC_LENGTH * Math.max(0, Math.min(100, band.share)) / 100;
      const at = start;
      start += length;
      // The middle of the segment on the arc, as a share of the svg's box, which is where its
      // tooltip is pinned. The arc runs from the left end over the top, so a fraction f of the way
      // along is the angle pi * (1 - f) from the right-hand end.
      const angle = Math.PI * (1 - (at + length / 2) / ARC_LENGTH);
      const x = ((CX + R * Math.cos(angle)) / VIEW_WIDTH) * 100;
      const y = ((CY - R * Math.sin(angle)) / VIEW_HEIGHT) * 100;
      return { fill: colourOf(index), length, start: at, x, y };
    });

    if (isNet) {
      const net = netOfBands(bands, topFirst);
      if (net === null) {
        this.problems.push('A net needs at least two bands: one favourable, one not.');
        return;
      }
      // Unsigned when positive: the gauge is the sign. A leading + on a figure this size reads as
      // part of the number, and the ring beside it already says which way the score fell.
      this.computed = String(Math.round(net));
      // Broken after "minus %" rather than left to wrap where it likes: the two halves are the
      // same shape, so the line ending on the operator makes the subtraction readable at a glance.
      const top = ordered[0];
      const bottom = ordered[ordered.length - 1];
      this.setNote(`% ${describe(top)} minus %\n${describe(bottom)}`, series.base);
      return;
    }

    const at = Math.min(Math.max(0, Math.floor(extra.shareBand ?? 0)), ordered.length - 1);
    this.computed = `${Math.round(ordered[at].share)}%`;
    this.setNote(`% ${describe(ordered[at])}`, series.base);
  }

  /**
   * No note under a figure somebody typed over the computed one. The note says how the computed
   * figure was worked out, so under a different number it explains a figure that is not on the card.
   * Kept where what was typed is the same number -- a "+61" over a computed 61 -- since the note is
   * still true of it. Said while editing, so a note that vanished is not a mystery.
   */
  private dropNoteUnderTypedFigure() {
    const typed = this.value.trim();
    if (!typed || !this.computed || !this.footnote) return;
    if (Number.parseFloat(typed) === Number.parseFloat(this.computed)) return;
    this.footnote = '';
    this.problems.push(`The score is typed over the computed ${this.computed}, so the note on how it `
      + 'was worked out is hidden. Clear the score to show the computed one and its note.');
  }

  /** The definition in brackets, as an aside to the figure, with the base after it. */
  private setNote(definition: string, base: number) {
    const hidden = this.block.reportType === 'kpi-score'
      && !!this.block.reportExtraData.hideLabels;
    if (hidden) return;
    this.footnote = `(${definition}). Base n=${base}.`;
  }

  set(field: 'value' | 'label' | 'caption', value: string) {
    this.onPatch?.({ [field]: value });
  }

}

/**
 * Longest an answer range is written into the note. "8-10" and "Getting better" fit; a scale worded
 * as sentences does not, and two of them made the note longer than the card it explains.
 */
const NOTE_RANGE_MAX = 20;

/**
 * "Promoters 8-10" -- the band's name and the answers it actually covered, or the name alone where
 * the answers are too long to print. An unnamed band keeps its answers whatever their length: they
 * are all there is to say what it is.
 *
 * No brackets of its own: the whole definition is already parenthesised, and a bracket inside a
 * bracket reads as a nesting the sentence does not have.
 */
function describe(band: Band): string {
  if (!band.range) return band.label;
  const named = !!band.label.trim();
  if (named && band.range.length > NOTE_RANGE_MAX) return band.label;
  return named ? `${band.label} ${band.range}` : band.range;
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * "Momentum" reads as "Positive momentum", but "NPS" and "Purchase Intent" are left as they are: a
 * word carrying capitals past its first letter is a name or an acronym, and lowering it misspells it.
 */
function lowerFirst(word: string): string {
  const rest = word.slice(1);
  return rest === rest.toLowerCase() ? word.charAt(0).toLowerCase() + rest : word;
}
